import './tokens-extra.css';
import { clsx } from 'clsx';
import {
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { Icon } from '@/app/kit';
import type { IconName } from '@/app/module';
import { haptic } from '@/lib/haptics';
import {
  type ButtonSize,
  type ButtonVariant,
  BUTTON_SIZE_CLASS as SIZE,
  BUTTON_VARIANT_CLASS as VARIANT,
} from './Button';
import { uiCopy } from './copy';

/**
 * Press and hold to confirm (end game, void, leave the pad). Works with sweaty fingers better
 * than a tiny dialog. A fill grows left to right while held; letting go or sliding off cancels.
 * Keyboard: hold Enter or Space. Screen readers (which send a click with no pointer): the first
 * activation arms it, the second within 4 s confirms.
 *
 *   <HoldToConfirm label="Hold to end game" onConfirm={endGame} />
 *   <HoldToConfirm label="Hold to void" ms={1200} variant="secondary" icon="close" onConfirm={voidIt} />
 */

export interface UseHoldOptions {
  ms: number;
  onComplete: () => void;
  disabled?: boolean;
}

/** Hold timer: start() begins, cancel() stops, onComplete fires once after `ms` of holding. */
export function useHold({ ms, onComplete, disabled = false }: UseHoldOptions) {
  const [holding, setHolding] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const done = useRef(onComplete);
  done.current = onComplete;

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setHolding(false);
  }, []);

  const start = useCallback(() => {
    if (disabled || timer.current) return;
    setHolding(true);
    timer.current = setTimeout(() => {
      timer.current = null;
      setHolding(false);
      done.current();
    }, ms);
  }, [ms, disabled]);

  useEffect(() => cancel, [cancel]);
  useEffect(() => {
    if (disabled) cancel();
  }, [disabled, cancel]);

  return { holding, start, cancel };
}

const ARM_MS = 4000;

export interface HoldToConfirmProps {
  label: string;
  onConfirm: () => void;
  /** Hold time in ms. Default 800. */
  ms?: number;
  holdingLabel?: string;
  armedLabel?: string;
  /** Read by screen readers as the button's description. */
  hint?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  block?: boolean;
  disabled?: boolean;
  className?: string;
}

const isActivationKey = (k: string) => k === 'Enter' || k === ' ' || k === 'Spacebar';

export function HoldToConfirm({
  label,
  onConfirm,
  ms = 800,
  holdingLabel = uiCopy.hold.holding,
  armedLabel = uiCopy.hold.armed,
  hint = uiCopy.hold.hint,
  variant = 'danger',
  size = 'md',
  icon,
  block,
  disabled,
  className,
}: HoldToConfirmProps) {
  const id = useId();
  const [armed, setArmed] = useState(false);
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const disarm = useCallback(() => {
    if (armTimer.current) clearTimeout(armTimer.current);
    armTimer.current = null;
    setArmed(false);
  }, []);
  useEffect(() => disarm, [disarm]);

  const confirm = useCallback(() => {
    disarm();
    haptic('stamp');
    onConfirm();
  }, [onConfirm, disarm]);

  const { holding, start, cancel } = useHold({ ms, onComplete: confirm, disabled });

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!isActivationKey(e.key)) return;
    e.preventDefault();
    if (!e.repeat) start();
  };
  const onKeyUp = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!isActivationKey(e.key)) return;
    e.preventDefault();
    cancel();
  };
  // detail 0 means no pointer was involved: a screen reader or switch control activated it.
  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (disabled || e.detail !== 0) return;
    if (armed) {
      confirm();
      return;
    }
    setArmed(true);
    armTimer.current = setTimeout(disarm, ARM_MS);
  };

  const text = holding ? holdingLabel : armed ? armedLabel : label;

  return (
    <button
      type="button"
      disabled={disabled}
      aria-describedby={`${id}-hint`}
      data-holding={holding || undefined}
      data-armed={armed || undefined}
      onPointerDown={(e) => {
        if (e.button === 0) start();
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={cancel}
      onClick={onClick}
      className={clsx(
        'tts-btn tts-no-select relative overflow-hidden',
        VARIANT[variant],
        SIZE[size],
        block && 'w-full',
        armed && 'outline-3 outline-offset-2 outline-ink outline-dashed',
        className,
      )}
      style={{ '--tts-hold-ms': `${ms}ms` } as CSSProperties}
    >
      <span aria-hidden="true" className="tts-ui-hold-fill" data-holding={holding ? '' : undefined} />
      {icon && <Icon name={icon} size={size === 'xl' ? 28 : 22} className="relative" />}
      <span className="relative" aria-live="polite">
        {text}
      </span>
      <span id={`${id}-hint`} className="sr-only">
        {hint}
      </span>
    </button>
  );
}
